import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

import type { Role } from "../lib/api";
import { cx } from "../lib/cx";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

const inputIcon = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 6h16M4 12h10M4 18h7" />
    <path d="M17.5 15.5v5M20 18h-5" />
  </svg>
);

const dashboardIcon = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 17l5-6 4 3 5-7 4 4" />
    <path d="M3 21h18" />
  </svg>
);

const masterIcon = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <path d="M3 9h18M9 9v11" />
  </svg>
);

const dailyIcon = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M3 10h18M8 3v4M16 3v4M8 14h3" />
  </svg>
);

const weeklyIcon = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M3 10h18M8 3v4M16 3v4M8 14h3M13 14h3" />
  </svg>
);

const teamIcon = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20a6 6 0 0 1 12 0" />
    <path d="M16 5.5a3 3 0 0 1 0 5M18 20a6 6 0 0 0-2.5-4.9" />
  </svg>
);

const NAV_BY_ROLE: Record<Role, NavItem[]> = {
  leader: [{ to: "/input", label: "Input Data", icon: inputIcon }],
  admin: [
    { to: "/admin/dashboard", label: "Dashboard", icon: dashboardIcon },
    { to: "/admin/master", label: "Master Data", icon: masterIcon },
    { to: "/admin/harian", label: "Harian", icon: dailyIcon },
    { to: "/admin/mingguan", label: "Mingguan", icon: weeklyIcon },
    { to: "/admin/tim", label: "Data Tim", icon: teamIcon },
  ],
};

export interface SidebarProps {
  role: Role;
  onNavigate?: () => void;
  className?: string;
}

export function Sidebar({ role, onNavigate, className }: SidebarProps) {
  const items = NAV_BY_ROLE[role];

  return (
    <nav
      aria-label="Navigasi utama"
      className={cx("flex h-full flex-col gap-8 p-6", className)}
    >
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-control accent-gradient text-lg font-black text-white shadow-clay">
          LD
        </span>
        <div>
          <p className="text-sm font-bold leading-tight text-text">Dashboard Leader</p>
          <p className="text-xs text-muted">Laporan tim & data</p>
        </div>
      </div>

      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end
              onClick={onNavigate}
              className={({ isActive }) =>
                cx(
                  "flex items-center gap-3 rounded-control px-4 py-3 text-sm font-semibold transition-all duration-150",
                  isActive
                    ? "accent-gradient text-white shadow-clay"
                    : "text-muted hover:bg-bg hover:text-text",
                )
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>

      <p className="mt-auto text-xs leading-relaxed text-muted">
        Sumber data tetap Google Sheet. Aplikasi ini hanya membaca dan menambah baris pada kolom
        input.
      </p>
    </nav>
  );
}
